import { Award, Users, Wrench, MapPin, Clock } from "lucide-react";

const STATS = [
  { n: "15+", l: "Jahre Erfahrung" },
  { n: "25.000+", l: "Reparaturen" },
  { n: "120+", l: "5-Stern Bewertungen" },
  { n: "60 Min", l: "Ø Express Reparatur" },
];

export default function AboutPage() {
  return (
    <div className="pt-32 pb-24 bg-[#0A0A0B] min-h-screen" data-testid="about-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* HERO ROW */}
        <div className="grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Über Uns</div>
            <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tight text-white mt-3 leading-[1.02]">
              Erleben,<br/>
              <span className="text-zinc-500">was verbindet.</span>
            </h1>
            <p className="text-xl text-zinc-400 mt-6 leading-relaxed">
              Seit 2023 in der Hernalser Hauptstraße. Wir sind kein Konzern — wir sind Nachbarn, die Smartphones lieben und unsere Kunden noch lieber.
            </p>
            <p className="text-base text-zinc-400 mt-4 leading-relaxed">
              Was uns auszeichnet? Wir nehmen uns Zeit. Wir reparieren ehrlich. Wir verkaufen nur, was Sie wirklich brauchen. Und wenn etwas nicht zu reparieren ist, sagen wir Ihnen das — kostenlos.
            </p>
          </div>
          <div className="md:col-span-5">
            {/* Brand graphic — Erleben, was verbindet */}
            <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-[#0A0A0B] relative group" data-testid="about-interior-image">
              <img
                src="/images/store/hdh-welt-illustration.png"
                alt="Die ganze Welt von Haus des Handys — Erleben, was verbindet"
                className="w-full h-full object-contain transition-transform duration-[1200ms] ease-out group-hover:scale-[1.02]"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-3xl pointer-events-none"></div>
            </div>
          </div>
        </div>

        {/* STATS */}
        <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map((s, i) => (
            <div key={i} className="text-center" data-testid={`stat-${i}`}>
              <div className="font-display text-5xl md:text-6xl font-bold text-white numeric">{s.n}</div>
              <div className="text-sm text-zinc-500 uppercase tracking-wider mt-2">{s.l}</div>
            </div>
          ))}
        </div>

        {/* VALUES */}
        <div className="mt-24 grid md:grid-cols-3 gap-6">
          <div className="p-8 rounded-2xl bg-[#141417] border border-white/5">
            <Wrench className="w-8 h-8 text-[#FF2D40]" />
            <h3 className="font-display text-xl font-semibold mt-4 text-white">Handwerk seit 2023</h3>
            <p className="text-sm text-zinc-400 mt-2 leading-relaxed">Über 25.000 reparierte Geräte. Erfahrung, die man spürt.</p>
          </div>
          <div className="p-8 rounded-2xl bg-[#141417] border border-white/5">
            <Award className="w-8 h-8 text-[#FF2D40]" />
            <h3 className="font-display text-xl font-semibold mt-4 text-white">A1 & MTel Partner</h3>
            <p className="text-sm text-zinc-400 mt-2 leading-relaxed">Autorisierter A1 Exclusive Partner & MTel Vertriebspartner — Tarife, Verträge & Wertgarantie direkt im Shop.</p>
          </div>
          <div className="p-8 rounded-2xl bg-[#141417] border border-white/5">
            <Users className="w-8 h-8 text-[#FF2D40]" />
            <h3 className="font-display text-xl font-semibold mt-4 text-white">Persönlich, nicht anonym</h3>
            <p className="text-sm text-zinc-400 mt-2 leading-relaxed">Sie kommen rein, wir kennen Sie beim Namen. So einfach ist das.</p>
          </div>
        </div>

        {/* CONTACT BLOCK */}
        <div className="mt-16 p-10 md:p-16 rounded-3xl bg-gradient-to-br from-[#180409] to-[#0F0F12] border border-white/5 text-white grid md:grid-cols-2 gap-10">
          <div>
            <MapPin className="w-8 h-8 text-[#FF2D40]" />
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-4">Besuchen Sie uns in Wien.</h2>
            <p className="text-zinc-400 mt-3 leading-relaxed">
              Hernalser Hauptstraße 135<br/>
              1170 Wien, Österreich
            </p>
            <a
              href="https://www.google.com/maps/place/Hernalser+Hauptstra%C3%9Fe+135,+1170+Wien"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 mt-5 px-5 h-11 rounded-full bg-white text-[#0A0A0B] font-medium btn-lift hover:bg-zinc-100 transition-colors"
              data-testid="about-maps-link"
            >
              In Maps öffnen
            </a>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-[#FF2D40]">
              <Clock className="w-4 h-4" />
              <span className="text-[10px] uppercase tracking-[0.22em] font-semibold">Öffnungszeiten</span>
            </div>
            <div className="flex justify-between text-sm border-b border-white/10 pb-2">
              <span className="text-zinc-400">Montag – Freitag</span><span className="font-medium text-white">09:00 – 19:00</span>
            </div>
            <div className="flex justify-between text-sm border-b border-white/10 pb-2">
              <span className="text-zinc-400">Samstag</span><span className="font-medium text-white">09:00 – 16:00</span>
            </div>
            <div className="flex justify-between text-sm border-b border-white/10 pb-2">
              <span className="text-zinc-500">Mittagspause</span><span className="font-medium text-zinc-500">12:30 – 13:00</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-400">Sonntag</span><span className="font-medium text-zinc-600">Geschlossen</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
